<template>
  <div
    class="cursor-pointer flex-row items-center justify-center rounded-md border bg-white px-2 py-2 font-bold dark:bg-slate-700 dark:text-gray-300"
    @click="isOpen = true"
  >
    <FontAwesomeIcon fixed-width :icon="['fal', 'calculator']" />
  </div>
  <Dialog
    :open="isOpen"
    class="relative z-100"
    as="div"
    @close="isOpen = false"
  >
    <div class="fixed inset-0 flex w-screen items-center justify-center p-4">
      <div
        class="fixed inset-0 bg-black/10"
        aria-hidden="true"
        @click="isOpen = false"
      />
      <DialogPanel
        class="z-100 flex min-h-56 w-120 flex-col gap-2 rounded-2xl bg-white p-5 pt-4 shadow-2xl"
      >
        <DialogTitle
          class="flex flex-row px-2 py-2 font-bold dark:text-slate-300"
        >
          <div class="flex-1 text-xl font-bold">
            {{ $t('global.price_calculator') }}
          </div>
          <button type="button" @click="isOpen = false">
            <FontAwesomeIcon icon="times" />
          </button>
        </DialogTitle>

        <div class="flex flex-col gap-4">
          <div>
            <WalkmanButton
              v-model="viewCalculate"
              :options="viewOptions"
              w-full
            />
          </div>
          <div class="relative text-right">
            <span
              class="absolute inset-y-0 right-2 content-center text-slate-400"
            >
              {{ currency === 'CHF' ? 'CHF' : '€' }}
            </span>
            <BaseInput
              v-model="price"
              name="price"
              type="number"
              input-class="text-right pr-6"
              @click="$event.target.select()"
            />
            <!--              input-class="text-right pr-2"-->
          </div>
          <div class="flex flex-col gap-4">
            <div
              v-for="vat_rate in vatRatesOrderd"
              :key="vat_rate"
              class="relative flex flex-row gap-4 rounded-md border border-slate-200 bg-slate-50 p-3"
            >
              <span
                class="absolute -top-2.5 rounded bg-white/75 px-1 font-semibold"
              >
                {{ vat_rate }}%
              </span>
              <div
                class="relative min-w-0 flex-1"
                :class="{
                  'order-3': viewCalculate === 'calculate_excluding_tax',
                  'order-1': viewCalculate === 'calculate_including_tax'
                }"
              >
                <label
                  class="absolute -top-2 ml-2 rounded bg-white/75 px-1 text-xs"
                >
                  {{ $t('global.excluding_vat') }}
                </label>
                <span
                  class="absolute inset-y-0 right-2 content-center text-slate-400"
                >
                  {{ currency === 'CHF' ? 'CHF' : '€' }}
                </span>
                <input
                  v-model="priceHtva[vat_rate]"
                  type="text"
                  class="w-full rounded border bg-white py-1 pl-2 text-right"
                  :class="currency === 'CHF' ? 'pr-10' : 'pr-5'"
                  readonly
                  @click="$event.target.select()"
                />
              </div>
              <div class="relative order-2 min-w-0 flex-1">
                <label
                  class="absolute -top-2 ml-2 rounded bg-white/75 px-1 text-xs"
                >
                  {{ $t('global.vat') }}
                </label>
                <span
                  class="absolute inset-y-0 right-2 content-center text-slate-400"
                >
                  {{ currency === 'CHF' ? 'CHF' : '€' }}
                </span>
                <input
                  v-model="tvaValue[vat_rate]"
                  type="text"
                  class="w-full rounded border bg-white py-1 pl-2 text-right"
                  :class="currency === 'CHF' ? 'pr-10' : 'pr-5'"
                  readonly
                  @click="$event.target.select()"
                />
              </div>
              <div
                class="relative min-w-0 flex-1"
                :class="{
                  'order-1': viewCalculate === 'calculate_excluding_tax',
                  'order-3': viewCalculate === 'calculate_including_tax'
                }"
              >
                <label
                  class="absolute -top-2 ml-2 rounded bg-white/75 px-1 text-xs"
                >
                  {{ $t('global.tax_included') }}
                </label>
                <span
                  class="absolute inset-y-0 right-2 content-center text-slate-400"
                >
                  {{ currency === 'CHF' ? 'CHF' : '€' }}
                </span>
                <input
                  v-model="priceTvac[vat_rate]"
                  type="text"
                  class="w-full rounded border bg-white py-1 pl-2 text-right"
                  :class="currency === 'CHF' ? 'pr-10' : 'pr-5'"
                  readonly
                  @click="$event.target.select()"
                />
              </div>
            </div>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script>
import WalkmanButton from '@c/WalkmanButton.vue'
import { mapGetters } from 'vuex'
import BaseInput from '@c/addf-package/components/BaseInput/BaseInput.vue'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'PriceCalculationModal',
  components: {
    Dialog,
    DialogTitle,
    FontAwesomeIcon,
    DialogPanel,
    BaseInput,
    WalkmanButton
  },
  data() {
    return {
      isOpen: false,
      viewCalculate: 'calculate_excluding_tax',
      price: 0,
      priceHtva: {},
      priceTvac: {},
      tvaValue: {}
    }
  },
  computed: {
    ...mapGetters({
      vatRates: 'metadata/getSelectedVatRatesInSettingsMetadata',
      currency: 'metadata/getDefaultCurrencyMetadata'
    }),
    vatRatesOrderd() {
      return this.vatRates.sort((a, b) => a - b)
    },
    viewOptions() {
      return [
        {
          value: 'calculate_excluding_tax',
          label: this.$t('button.calculate_price_excluding_vat')
        },
        {
          value: 'calculate_including_tax',
          label: this.$t('button.calculate_price_including_vat')
        }
      ]
    }
  },
  watch: {
    price(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.calculate()
      }
    },
    viewCalculate(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.reset()
        this.calculate()
      }
    }
  },
  mounted() {
    this.reset()
  },
  onErrorCaptured(err) {
    return false
  },
  methods: {
    reset() {
      this.vatRatesOrderd.forEach((vat_rate) => {
        this.priceHtva[vat_rate.toString()] = 0
        this.priceTvac[vat_rate.toString()] = 0
        this.tvaValue[vat_rate.toString()] = 0
      })
    },
    calculate() {
      if (this.viewCalculate === 'calculate_excluding_tax') {
        this.vatRatesOrderd.forEach((vat_rate) => {
          let newValue = parseFloat(this.price)
          this.updateFromTVAC(newValue, vat_rate)
        })
      } else if (this.viewCalculate === 'calculate_including_tax') {
        this.vatRatesOrderd.forEach((vat_rate) => {
          let newValue = parseFloat(this.price)
          this.updateFromHTVA(newValue, vat_rate)
        })
      }
    },
    updateFromTVAC(value, vat_rate) {
      let htvaValue = Math.round((value / (1 + vat_rate / 100)) * 100) / 100

      this.priceHtva[vat_rate.toString()] = htvaValue
        .toFixed(2)
        .replace('.', ',')
      this.priceTvac[vat_rate.toString()] = value.toFixed(2).replace('.', ',')
      this.tvaValue[vat_rate.toString()] = (value - htvaValue)
        .toFixed(2)
        .replace('.', ',')
    },
    updateFromHTVA(value, vat_rate) {
      let tvacValue = Math.round(value * (1 + vat_rate / 100) * 100) / 100

      this.priceHtva[vat_rate.toString()] = value.toFixed(2).replace('.', ',')
      this.priceTvac[vat_rate.toString()] = tvacValue
        .toFixed(2)
        .replace('.', ',')
      this.tvaValue[vat_rate.toString()] = (tvacValue - value)
        .toFixed(2)
        .replace('.', ',')
    }
  }
}
</script>

<style scoped></style>
